
import Papa from 'papaparse';
import * as XLSX from 'xlsx';

export const exportDescriptionsToCSV = (
  descriptions: { 
    originalText: string; 
    generatedDescription: string; 
    articleNumber?: string;
    productDetails?: string;
    showProductDetails?: boolean;
    // Optional translated product name support
    translatedProductName?: string;
    showTranslatedProductName?: boolean;
  }[],
  fileNamePrefix: string = 'product-descriptions'
) => {
  // Validate input data
  if (!descriptions || descriptions.length === 0) {
    throw new Error('No data to export');
  }

  // Log what we're exporting to help with debugging
  console.log("Exporting descriptions with product details:", descriptions);
  
// Check if we should include optional columns
const showProductDetails = descriptions.length > 0 && descriptions[0].showProductDetails === true;
const showTranslatedProductName = descriptions.length > 0 && descriptions[0].showTranslatedProductName === true;

// Prepare data with proper null handling
const exportData = descriptions.map(desc => {
  const baseData: Record<string, string> = {
    'Article Number': desc.articleNumber || ''
  };

  if (showTranslatedProductName) {
    baseData['Translated Product Name'] = desc.translatedProductName || '';
  } else {
    baseData['Product Name'] = desc.originalText || '';
  }

  // Generated description always included after names
  baseData['Generated Description'] = desc.generatedDescription || '';
  
  if (showProductDetails) {
    baseData['Product Details'] = desc.productDetails || '';
  }
  
  return baseData;
});

  // Make sure product details are properly formatted with line break preservation
  const csv = Papa.unparse(exportData, {
    quotes: true,   // Ensure all fields are quoted to handle line breaks properly
    newline: '\r\n' // Use Windows-style line breaks for Excel compatibility
  });

  // Create blob and download
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', `${fileNamePrefix}-${new Date().toISOString().split('T')[0]}.csv`);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const exportDescriptionsToExcel = (
  descriptions: { 
    originalText: string; 
    generatedDescription: string; 
    articleNumber?: string;
    productDetails?: string;
    showProductDetails?: boolean;
    // Optional translated product name support
    translatedProductName?: string;
    showTranslatedProductName?: boolean;
  }[],
  fileNamePrefix: string = 'product-descriptions'
) => {
  // Validate input data
  if (!descriptions || descriptions.length === 0) {
    throw new Error('No data to export');
  }

  // Log what we're exporting
  console.log("Exporting descriptions to Excel:", descriptions);
  
// Check if we should include optional columns
const showProductDetails = descriptions.length > 0 && descriptions[0].showProductDetails === true;
const showTranslatedProductName = descriptions.length > 0 && descriptions[0].showTranslatedProductName === true;

// Format data for Excel with proper null handling and column ordering
const data = descriptions.map(desc => {
  const baseData: Record<string, string> = {
    'Article Number': desc.articleNumber || ''
  };
  
  if (showTranslatedProductName) {
    baseData['Translated Product Name'] = desc.translatedProductName || '';
  } else {
    baseData['Product Name'] = desc.originalText || '';
  }
  
  baseData['Generated Description'] = desc.generatedDescription || '';
  
  if (showProductDetails) {
    baseData['Product Details'] = desc.productDetails || '';
  }
  
  return baseData;
});

  // Create a new workbook
  const wb = XLSX.utils.book_new();
  
  // Convert data to worksheet
  const ws = XLSX.utils.json_to_sheet(data);
  
  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(wb, ws, 'Product Descriptions');
  
// Adjust column widths for better readability
const columnWidths = [
  { wch: 15 }, // Article Number
];

if (showTranslatedProductName) {
  columnWidths.push({ wch: 30 }); // Translated Product Name
} else {
  columnWidths.push({ wch: 30 }); // Product Name
}

columnWidths.push({ wch: 60 }); // Generated Description

// Add product details column width only if showing product details
if (showProductDetails) {
  columnWidths.push({ wch: 40 }); // Product Details
}

ws['!cols'] = columnWidths;
  
// Set specific cell formatting to preserve line breaks
// Get worksheet range
const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');

// Determine column indexes dynamically by header names
const headers = Object.keys(data[0] || {});
const genDescIndex = headers.indexOf('Generated Description');
const detailsIndex = headers.indexOf('Product Details');

// Apply text wrap formatting to Product Details column if present
if (detailsIndex >= 0) {
  for (let row = range.s.r; row <= range.e.r; row++) {
    const cellRef = XLSX.utils.encode_cell({ r: row, c: detailsIndex });
    if (!ws[cellRef]) continue;
    if (!ws[cellRef].s) ws[cellRef].s = {} as any;
    ws[cellRef].s = { 
      ...ws[cellRef].s,
      alignment: { 
        wrapText: true,
        vertical: 'top'
      }
    } as any;

    if (typeof ws[cellRef].v === 'string') {
      const cellValue = String(ws[cellRef].v);
      if (cellValue.includes('\n') && !cellValue.includes('\r\n')) {
        ws[cellRef].v = cellValue.replace(/\n/g, '\r\n');
      }
    }
  }
}

// Apply similar formatting to Generated Description column
if (genDescIndex >= 0) {
  for (let row = range.s.r; row <= range.e.r; row++) {
    const cellRef = XLSX.utils.encode_cell({ r: row, c: genDescIndex });
    if (!ws[cellRef]) continue;
    if (!ws[cellRef].s) ws[cellRef].s = {} as any;
    ws[cellRef].s = { 
      ...ws[cellRef].s,
      alignment: { 
        wrapText: true,
        vertical: 'top'
      }
    } as any;

    if (typeof ws[cellRef].v === 'string') {
      const cellValue = String(ws[cellRef].v);
      if (cellValue.includes('\n') && !cellValue.includes('\r\n')) {
        ws[cellRef].v = cellValue.replace(/\n/g, '\r\n');
      }
    }
  }
}

// Set row height to accommodate wrapped text
const rowHeights = [] as any[];
for (let i = 0; i <= range.e.r; i++) {
  rowHeights.push({ hpx: 50 }); // Set default height for all rows
}
ws['!rows'] = rowHeights;
  
  // Generate Excel file
  const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  
  // Create blob and download
  const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', `${fileNamePrefix}-${new Date().toISOString().split('T')[0]}.xlsx`);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
